import { z } from 'zod';
import {
  currencySchema,
  invoiceItemSourceSchema,
  invoiceStatusSchema,
  paymentMethodSchema,
  type Currency,
  type InvoiceStatus,
  type PaymentMethod,
} from './enums.js';

/** Billing contracts (docs/spec.md §4.3, §7 screen 5, §8.3). */

export const serviceInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  nameLa: z.string().trim().min(1).max(160),
  nameEn: z.string().trim().max(160).optional().or(z.literal('')),
  category: z.string().trim().max(80).optional().or(z.literal('')),
  price: z.coerce.number().int().min(0).default(0),
});
export type ServiceInput = z.infer<typeof serviceInputSchema>;
export type ServiceInputForm = z.input<typeof serviceInputSchema>;

export const serviceUpdateSchema = serviceInputSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type ServiceUpdate = z.infer<typeof serviceUpdateSchema>;

export interface Service {
  id: number;
  code: string;
  nameLa: string;
  nameEn: string | null;
  category: string | null;
  price: number;
  isActive: boolean;
}

export const invoiceItemInputSchema = z.object({
  sourceType: invoiceItemSourceSchema.default('custom'),
  /** id of the service or drug this line came from, when it came from one. */
  sourceId: z.coerce.number().int().positive().optional(),
  description: z.string().trim().min(1).max(255),
  qty: z.coerce.number().int().min(1).max(100_000).default(1),
  unitPrice: z.coerce.number().int().min(0).default(0),
  /** The "ໝາຍເຫດ/remark" column on the clinic's printed receipt. */
  remark: z.string().trim().max(255).optional().or(z.literal('')),
});
export type InvoiceItemInput = z.infer<typeof invoiceItemInputSchema>;

export const invoiceInputSchema = z.object({
  patientId: z.coerce.number().int().positive(),
  /** Signs the "ແພດປິ່ນປົວ" line on the printed receipt. */
  doctorId: z.coerce.number().int().positive().optional(),
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date_format'),
  diagnosis: z.string().trim().max(255).optional().or(z.literal('')),
  discount: z.coerce.number().int().min(0).default(0),
  note: z.string().trim().max(2000).optional().or(z.literal('')),
  items: z.array(invoiceItemInputSchema).min(1, 'items_required'),
  /** What the customer is quoted in — the ledger total stays in kip regardless. */
  currency: currencySchema.default('LAK'),
});
export type InvoiceInput = z.infer<typeof invoiceInputSchema>;
export type InvoiceInputForm = z.input<typeof invoiceInputSchema>;

export const paymentInputSchema = z.object({
  amount: z.coerce.number().int().min(1),
  method: paymentMethodSchema.default('cash'),
  note: z.string().trim().max(255).optional().or(z.literal('')),
});
export type PaymentInput = z.infer<typeof paymentInputSchema>;

export interface InvoiceLine {
  id: number;
  lineNo: number;
  sourceType: 'service' | 'drug' | 'lab' | 'custom';
  sourceId: number | null;
  description: string;
  qty: number;
  unitPrice: number;
  amount: number;
  remark: string | null;
}

export interface InvoicePayment {
  id: number;
  amount: number;
  method: PaymentMethod;
  paidAt: string;
  receivedByName: string | null;
  note: string | null;
}

export interface Invoice {
  id: number;
  no: string;
  extNo: string | null;
  patientId: number;
  patientName: string;
  patientCode: string;
  patientAge: number | null;
  patientPhone: string | null;
  patientAddress: string | null;
  patientOccupation: string | null;
  doctorId: number | null;
  doctorName: string | null;
  issuedByName: string | null;
  issueDate: string;
  issuePlace: string | null;
  diagnosis: string | null;
  subtotal: number;
  discount: number;
  total: number;
  paidAmount: number;
  status: InvoiceStatus;
  currency: Currency;
  /** Kip per 1 unit of `currency`, snapshotted at issue time — 1 when `currency` is LAK. */
  fxRate: number;
  note: string | null;
  items: InvoiceLine[];
  payments: InvoicePayment[];
}

export interface InvoiceSummary {
  totalRevenue: number;
  outstanding: number;
  paidCount: number;
}

/**
 * External receipt number: `INTRN` + YYYYMMDD + a 5-digit sequence that restarts
 * each day (confirmed with the clinic 2026-07-30).
 *
 * Note: the sample on their paper form, INTRN20260500010, has 11 digits rather
 * than 13 — the previous system evidently numbered differently. New receipts
 * follow the format above.
 */
export const EXT_NO_PREFIX = 'INTRN';
export const EXT_NO_SEQ_WIDTH = 5;

export function extNoPrefixFor(date: string): string {
  return `${EXT_NO_PREFIX}${date.replaceAll('-', '')}`;
}

export function formatExtNo(date: string, sequence: number): string {
  return `${extNoPrefixFor(date)}${String(sequence).padStart(EXT_NO_SEQ_WIDTH, '0')}`;
}

/** Status derived from what has actually been paid. */
export function invoiceStatusFor(total: number, paidAmount: number): InvoiceStatus {
  if (paidAmount <= 0) return 'unpaid';
  if (paidAmount >= total) return 'paid';
  return 'partial';
}

export const invoiceStatusValues = invoiceStatusSchema;
